import { BlogPost } from "@/data/blogPosts";
import { PUBLISH_DATE_MAP } from "@/lib/canonicalSchedule";
import { normalizeCategory } from "@/lib/categoryNormalizer";

/**
 * Deduplicates blog posts by ID and slug.
 * When duplicates are found, the first occurrence is kept.
 * This prevents canonical conflicts and content cannibalization.
 */
export function deduplicatePosts(posts: BlogPost[]): BlogPost[] {
  const seenIds = new Set<string>();
  const seenSlugs = new Set<string>();
  const deduped: BlogPost[] = [];

  for (const post of posts) {
    if (seenIds.has(post.id) || seenSlugs.has(post.slug)) {
      continue;
    }
    seenIds.add(post.id);
    seenSlugs.add(post.slug);
    // Normalize the category during deduplication
    deduped.push({ ...post, category: normalizeCategory(post.category) });
  }

  return deduped;
}

/**
 * Parses a date string (ISO or human-readable) into a timestamp.
 * Invalid dates fall back to 0 so they sort oldest.
 */
function parseTimestamp(dateStr: string): number {
  const parsed = new Date(dateStr.replace(/,/g, "")).getTime();
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * SITE RULE: Blog listings are always ordered by most-recent-first.
 *
 * Sorts posts by their `date` field in descending order (newest at the top).
 * This is the single source of truth for blog ordering across the index,
 * category pages, and any derived "top" lists. Posts with equal dates keep
 * a stable order (array index) via a secondary sort on the original position.
 */
export function sortByDateDesc(posts: BlogPost[]): BlogPost[] {
  return posts
    .map((post, originalIndex) => ({ post, originalIndex }))
    .sort((a, b) => {
      const dateA = PUBLISH_DATE_MAP[a.post.slug] || a.post.date;
      const dateB = PUBLISH_DATE_MAP[b.post.slug] || b.post.date;
      const diff = parseTimestamp(dateB) - parseTimestamp(dateA);
      if (diff !== 0) return diff;
      return a.originalIndex - b.originalIndex; // stable tiebreak
    })
    .map((entry) => entry.post);
}

/**
 * CTA variants rotated by category to eliminate boilerplate duplicate content.
 * Each variant references the specific topic area of the post.
 */
const CTA_VARIANTS: Record<
  string,
  { title: string; body: string; cta: string }
> = {
  "Compensation & Logistics": {
    title: "Stop Losing Money to Payment Friction",
    body: "If your offshore payment process is costing you talent or margin, let's audit your financial infrastructure.",
    cta: "Book a Systems Audit",
  },
  "Hiring Strategy": {
    title: "Stop Hiring the Wrong Talent",
    body: "If your offshore hires keep breaking your systems, you need an architect, not a generalist. Let's fix your hiring strategy.",
    cta: "Book a Hiring Strategy Call",
  },
  "Systems Architecture": {
    title: "Stop Building Fragile Systems",
    body: "If your CRM breaks every time you scale, you need enterprise-grade architecture. Let's audit your infrastructure.",
    cta: "Book a Technical Architecture Audit",
  },
  "Revenue Operations": {
    title: "Stop Bleeding Revenue to Broken Systems",
    body: "If your CRM is a mess and your leads are falling through the cracks, you don't need another generic VA. You need a Revenue Operations Architect.",
    cta: "Book a Free Technical Systems Audit",
  },
  "Agency Scaling": {
    title: "Stop Scaling with Duct Tape",
    body: "If your agency breaks every time you add a client, your infrastructure needs engineering, not patches.",
    cta: "Book a Scaling Audit",
  },
  "Automation Strategy": {
    title: "Stop Relying on Fragile Automations",
    body: "If your Zapier zaps break weekly, it's time for enterprise-grade n8n orchestration. Let's architect your automation.",
    cta: "Book an Automation Audit",
  },
  "Hiring Guide": {
    title: "Stop Wasting Time on Bad Hires",
    body: "If your offshore talent keeps breaking your systems, you need a specialized architect. Let's discuss your hiring strategy.",
    cta: "Book a Hiring Consultation",
  },
  "Advanced Automation": {
    title: "Stop Losing Data to Silent Failures",
    body: "If your automations fail silently, you're losing leads and revenue. Let's engineer fault-tolerant infrastructure.",
    cta: "Book an Automation Audit",
  },
  "Artificial Intelligence": {
    title: "Stop Falling Behind on AI",
    body: "If your competitors are deploying AI agents and you're still using manual workflows, let's architect your AI infrastructure.",
    cta: "Book an AI Strategy Call",
  },
  "Conversion Optimization": {
    title: "Stop Losing Leads to Slow Response",
    body: "If your speed-to-lead is measured in hours, you're losing deals. Let's engineer instant response systems.",
    cta: "Book a Conversion Audit",
  },
  "CRM Architecture": {
    title: "Stop Operating with Dirty Data",
    body: "If your CRM is a black box of broken fields and duplicate records, let's architect a pristine data foundation.",
    cta: "Book a CRM Architecture Audit",
  },
  "Management & Tracking": {
    title: "Stop Micromanaging Your Remote Team",
    body: "If you're tracking keystrokes instead of system health, you need outcome-based management frameworks.",
    cta: "Book a Management Systems Audit",
  },
  "Infrastructure Scaling": {
    title: "Stop Breaking at Scale",
    body: "If your systems collapse under volume, you need enterprise-grade infrastructure. Let's architect for 10x growth.",
    cta: "Book an Infrastructure Audit",
  },
  default: {
    title: "Stop Bleeding Revenue to Broken Systems",
    body: "If your CRM is a mess and your leads are falling through the cracks, you don't need another generic VA. You need a Revenue Operations Architect.",
    cta: "Book a Free Technical Systems Audit",
  },
};

/**
 * Returns the appropriate CTA variant for a given category.
 */
export function getCTAVariant(category: string): {
  title: string;
  body: string;
  cta: string;
} {
  return CTA_VARIANTS[category] || CTA_VARIANTS["default"];
}

/**
 * Removes keyword stuffing patterns from blog post content.
 * - Removes <strong> tags wrapping exact-match keywords in first paragraph
 * - Converts them to natural language
 */
export function removeKeywordStuffing(content: string): string {
  // Pattern: <strong>exact match keyword phrase</strong> in the first paragraph
  // We'll remove the <strong> wrapper from phrases that look like exact-match keywords
  // (lowercase, hyphenated, or containing "how to" / "via" / "in the" etc.)
  return content.replace(
    /<strong>([a-z][a-z\s'-]{10,80})<\/strong>/g,
    (match, inner) => {
      // Only remove <strong> if the text looks like a stuffed keyword
      // (all lowercase, contains common keyword phrases, no proper nouns)
      if (
        inner === inner.toLowerCase() &&
        (inner.includes("how to") ||
          inner.includes("via ") ||
          inner.includes("in the ") ||
          inner.includes("from the ") ||
          inner.includes("for ") ||
          inner.includes("when ") ||
          inner.includes("what ") ||
          inner.includes("can ") ||
          inner.includes("why ") ||
          inner.includes("is ") ||
          inner.includes("best ") ||
          inner.includes("hire ") ||
          inner.includes("outsourcing ") ||
          inner.includes("difference between") ||
          inner.includes("standard ") ||
          inner.includes("fair ") ||
          inner.includes("hidden ") ||
          inner.includes("signs ") ||
          inner.includes("tasks ") ||
          inner.includes("examples of"))
      ) {
        return inner; // Remove <strong> wrapper, keep text
      }
      return match; // Keep <strong> for legitimate emphasis
    },
  );
}

/**
 * Replaces boilerplate CTA blocks with category-specific variants.
 * Detects the standard "Stop Bleeding Revenue to Broken Systems" block
 * and replaces it with a rotated variant.
 */
export function replaceBoilerplateCTA(
  content: string,
  category: string,
): string {
  const variant = getCTAVariant(category);

  // Pattern 1: The "Stop Bleeding Revenue" block with rounded-lg styling
  const pattern1 =
    /<div class="bg-card border border-border p-6 rounded-lg my-8 shadow-sm text-center">\s*<h3 class="text-xl font-bold text-white mb-2">Stop Bleeding Revenue to Broken Systems<\/h3>\s*<p class="text-slate-300 mb-4">If your CRM is a mess and your leads are falling through the cracks, you don't need another generic VA\. You need a Revenue Operations Architect\.<\/p>\s*<a href="\/#contact" class="inline-block bg-primary !text-white no-underline px-6 py-3 rounded-md font-semibold hover:bg-primary\/90 transition-colors">Book a Free Technical Systems Audit<\/a>\s*<\/div>/g;

  // Pattern 2: The "Stop Bleeding Revenue" block with rounded-2xl styling
  const pattern2 =
    /<div class="bg-card border border-border p-6 rounded-lg my-8 shadow-sm text-center">\s*<h3 class="text-xl font-bold text-white mb-2">Stop Bleeding Revenue to Broken Systems<\/h3>[\s\S]*?Book a Free Technical Systems Audit<\/a>\s*<\/div>/g;

  const replacement = `<div class="bg-card border border-border p-6 rounded-lg my-8 shadow-sm text-center">
        <h3 class="text-xl font-bold text-white mb-2">${variant.title}</h3>
        <p class="text-slate-300 mb-4">${variant.body}</p>
        <a href="/#contact" class="inline-block bg-primary !text-white no-underline px-6 py-3 rounded-md font-semibold hover:bg-primary/90 transition-colors">${variant.cta}</a>
      </div>`;

  let result = content.replace(pattern1, replacement);
  result = result.replace(pattern2, replacement);

  return result;
}

/**
 * Strips duplicate <h1> tags from the start of post content.
 * The BlogPost page header already renders an editorial <h1> from post.title,
 * so a raw <h1> at the top of content causes duplicate titles and unstyled inline text.
 */
export function stripDuplicateH1(content: string): string {
  if (!content) return "";
  // Strip opening <h1> tags at the start of content (with optional leading whitespace or tags)
  return content
    .replace(/^\s*<h1[^>]*>[\s\S]*?<\/h1>\s*/i, "")
    .replace(/^(\s*<[^>]+>\s*)*<h1[^>]*>[\s\S]*?<\/h1>\s*/i, "$1");
}

/**
 * Full post processor: strips duplicate <h1>, removes keyword stuffing, and replaces boilerplate CTAs.
 */
export function processBlogContent(content: string, category: string): string {
  let processed = stripDuplicateH1(content);
  processed = removeKeywordStuffing(processed);
  processed = replaceBoilerplateCTA(processed, category);
  return processed;
}
